<template>
  <div id="details" class="simulator-details p-xxxl bg-cobalt">
    <div class="simulator-details-wrapper wrapper">
      <button class="simulator-details__close" @click="onHide">
        <SvgSprite class="icon" symbol="ui-plus" size="36" role="img" />
        <span>{{ t('global.fermer') }}</span>
      </button>

      <h2 class="simulator-details__title h3">
        {{ content.title }}
      </h2>
      <div class="simulator-details__subtitle lead">
        {{ content.subtitle }}
        <small v-if="content.condition">
          *
          {{ content.condition }}
        </small>
      </div>

      <div class="simulator-details___content">
        <SimulatorAmounts
          v-for="item in content.items"
          :key="item.title"
          :content="item"
          class="simulator-details__content__item"
        />
      </div>

      <div class="simulator-details__footer">
        <h3 class="simulator-details__footer__title">
          {{ content.footer.title }}
        </h3>
        <p class="simulator-details__footer__savings h2">
          {{ content.footer.savings }}
        </p>
        <p class="simulator-details__footer__percent">
          <span> {{ content.footer.text }}</span>

          <small v-if="content.footer.smallText">{{
            content.footer.smallText
          }}</small>
        </p>

        <div
          v-if="content.orderLink || content.contactLink"
          class="simulator-details__footer__links"
        >
          <Action
            v-if="content.orderLink"
            :content="{ ...content.orderLink, modifiers: ['btn'] }"
            @click="onOrderClick"
          />
          <Action
            v-if="content.contactLink"
            :content="{ ...content.contactLink, modifiers: ['btn', 'black'] }"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'

import SimulatorAmounts from '@/components/simulator/Amounts.vue'
import { push } from '@/utils/tracking'

import type { PropType } from 'vue'
import type { Amounts } from '@/components/simulator/Amounts.vue'
import type { Link } from '@/types'

interface SimulatorDetails {
  orderLink: Link
  contactLink: Link
  title: string
  subtitle: string
  condition?: string
  items: Amounts[]
  footer: {
    title: string
    text: string
    smallText: string
    savings: string
  }
}

defineProps({
  content: {
    type: Object as PropType<SimulatorDetails>,
    required: true,
  },
})

const { t } = useI18n()
const emit = defineEmits<{
  hide: []
}>()

// Handle click on hide button
const onHide = () => emit('hide')

// Track click on order CTA
const onOrderClick = () =>
  push({
    event: 'simulator',
    eventCategory: 'simulator',
    eventAction: 'click',
    eventLabel: 'order',
  })
</script>

<style lang="scss" scoped>
$bp: l;

.simulator-details {
  --c-text-highlight: var(--c-accent);

  position: relative;
  background: $c-secondary;

  .simulator-eco & {
    --c-text-highlight: var(--c-text);

    background: var(--c-accent-20);
  }
}

.simulator-details__close {
  @extend %button-nostyle;

  position: absolute;
  top: 0;
  right: 0;
  padding: $spacing;
  color: var(--c-accent);
  cursor: pointer;
  transition: color 0.1s;

  .icon {
    fill: currentcolor;
    transform: rotate(45deg);
  }

  span {
    @extend %visually-hidden;
  }

  &:hover,
  &:focus-visible {
    color: var(--c-accent-40);
  }

  @include mq($bp) {
    top: $spacing * 3;
    right: col(1);
  }
}

.simulator-details__title {
  @extend %fw-bold;
  @extend %text-center;
}

.simulator-details__subtitle {
  @extend %text-center;

  margin: $spacing * 0.75 col(1) $spacing * 1.5;

  small {
    @extend %fw-normal;

    display: block;
    margin-top: $spacing * 0.25;
    color: var(--c-text-highlight);
  }

  @include mq($bp) {
    margin: $spacing col(3) $spacing * 2;
  }
}

// Content
.simulator-details___content {
  position: relative;
  margin-bottom: $spacing * 1.5;
  background: $c-white;
  border: 0.1rem solid var(--c-accent-border);
  border-radius: 1.5rem;

  &::after {
    @include triangle(bottom, 2.25rem);
    @include center-x;

    content: '';
    bottom: -2.25rem;
    color: $c-black;
  }

  @include mq($bp) {
    display: flex;
    margin: 0 col(1, var(--available-columns)) $spacing * 1.5;
  }
}

.simulator-details__content__item {
  overflow: hidden;

  &:last-child {
    border-radius: 1.5rem;
  }

  :deep(.simulator-amounts__list) {
    small {
      @extend %tiny-fs;

      display: block;
      margin-top: -0.2rem;
      color: $c-grey;
      line-height: 1.2;
    }
  }

  @include mq($bp) {
    display: flex;
    flex-direction: column;
    flex: 1;
    padding-top: 0;
    border-radius: 0 0 0 1.5rem;

    &:last-child {
      border-radius: 0 0 1.5rem;
    }

    :deep(.simulator-amounts__title) {
      padding-top: var(--padding);
    }
  }
}

// Footer
.simulator-details__footer {
  @extend %text-center;

  position: relative;
  margin: 0 auto;
  padding: $spacing * 2 col(1, var(--available-columns));
  background: $c-white;
  border: 0.1rem solid var(--c-accent-border);
  border-radius: 2rem;

  @include mq($bp) {
    margin: 0 col(4, var(--available-columns));
  }
}

.simulator-details__footer__title {
  @extend %fw-normal;

  color: var(--c-text-highlight);
  font-size: 1em;
  line-height: 1.7;
}

.simulator-details__footer__savings {
  @extend %fw-bold;

  margin-top: 0.5rem;
}

.simulator-details__footer__percent {
  margin: $spacing 0;

  /* stylelint-disable-next-line no-descending-specificity */
  small {
    display: block;
    margin-top: 0.5rem;
    color: var(--c-text-highlight);
  }

  .simulator-eco & {
    --c-text-highlight: #{$c-grey};
  }
}

.simulator-details__footer__links {
  display: flex;
  flex-flow: row wrap;
  justify-content: center;
  align-items: center;
  gap: $spacing * 0.5;
}
</style>
